import { register } from '!/repository'
import { type Staged } from '!/router'
import { api, LiveEventsTypes } from '@setplex/tria-api'
import { attach, createEvent, createStore, restore, sample } from 'effector'
import { combineEvents, not, or } from 'patronum'
import { reset } from 'patronum/reset'
import { options } from '~/core/flags'
import { model as eventsFeatureModel } from '~/features/events'
import { model as guest } from '~/features/subscriber/guest'
import { $liveEventsTranslations } from './translations'

export const initAllEventsPage = createEvent<{
  query: URLSearchParams
  stage: Staged
}>()

export const leftAllEventsPage = createEvent()

export const loadAllEvents = createEvent()

const initLiveEventsFx = attach({ effect: api.liveEvents.initLiveEventsFx })
const initUpcomingEventsFx = attach({
  effect: api.liveEvents.initUpcomingEventsFx,
})
const initPastEventsFx = attach({ effect: api.liveEvents.initPastEventsFx })

export const $hasLiveEvent = createStore<boolean>(false)
export const $hasUpcomingEvent = createStore<boolean>(false)
export const $hasPastEvent = createStore<boolean>(false)

/** Skeleton start*/
export const setContentLoading = createEvent<boolean>()

export const $contentLoading = restore(setContentLoading, false)

/** Skeleton end*/

sample({
  clock: initLiveEventsFx.finally,
  fn: (live) => live.status === 'done' && Boolean(live.result.content.length),
  target: $hasLiveEvent,
})
sample({
  clock: initUpcomingEventsFx.finally,
  fn: (live) => live.status === 'done' && Boolean(live.result.content.length),
  target: $hasUpcomingEvent,
})
sample({
  clock: initPastEventsFx.finally,
  fn: (live) => live.status === 'done' && Boolean(live.result.content.length),
  target: $hasPastEvent,
})

/********** Skeleton logics starts ***********/

const $isAnyInitFxPending = or(
  initLiveEventsFx.pending,
  initUpcomingEventsFx.pending,
  initPastEventsFx.pending
)

sample({
  clock: $isAnyInitFxPending,
  target: setContentLoading,
})
/********** Skeleton logics ends ***********/

sample({
  clock: initAllEventsPage,
  filter: not($isAnyInitFxPending),
  target: loadAllEvents,
})

sample({
  clock: loadAllEvents,
  filter: options.$liveEventRecordsEnabled,
  target: [initLiveEventsFx, initUpcomingEventsFx, initPastEventsFx],
})

sample({
  clock: loadAllEvents,
  filter: not(options.$liveEventRecordsEnabled),
  target: [initLiveEventsFx, initUpcomingEventsFx],
})

const allTabsLoadedWithPast = combineEvents({
  events: {
    live: initLiveEventsFx.finally,
    upcoming: initUpcomingEventsFx.finally,
    past: initPastEventsFx.finally,
  },
})

const allTabsLoadedWithoutPast = combineEvents({
  events: {
    live: initLiveEventsFx.finally,
    upcoming: initUpcomingEventsFx.finally,
  },
})
// first in order
sample({
  clock: allTabsLoadedWithPast,
  source: { live: $hasLiveEvent, redirectUrl: eventsFeatureModel.$redirectTab },
  filter: ({ live, redirectUrl }) =>
    live && redirectUrl === LiveEventsTypes.LIVE,
  fn: () => ({
    id: LiveEventsTypes.LIVE,
  }),
  target: eventsFeatureModel.changeActiveTab,
})

// second in order
sample({
  clock: allTabsLoadedWithPast,
  source: {
    live: $hasLiveEvent,
    upcoming: $hasUpcomingEvent,
    redirectUrl: eventsFeatureModel.$redirectTab,
  },
  filter: ({ upcoming, live, redirectUrl }) =>
    (upcoming && !live) || redirectUrl === LiveEventsTypes.UPCOMING,
  fn: () => ({
    id: LiveEventsTypes.UPCOMING,
  }),
  target: eventsFeatureModel.changeActiveTab,
})

// third in order
sample({
  clock: allTabsLoadedWithPast,
  source: {
    live: $hasLiveEvent,
    upcoming: $hasUpcomingEvent,
    past: $hasPastEvent,
    redirectUrl: eventsFeatureModel.$redirectTab,
  },
  filter: ({ upcoming, live, past, redirectUrl }) =>
    (past && !upcoming && !live) || redirectUrl === LiveEventsTypes.PAST,
  fn: () => ({
    id: LiveEventsTypes.PAST,
  }),
  target: eventsFeatureModel.changeActiveTab,
})

// first in order without past events
sample({
  clock: allTabsLoadedWithoutPast,
  source: {
    live: $hasLiveEvent,
    pastEventsEnabled: options.$liveEventRecordsEnabled,
    redirectUrl: eventsFeatureModel.$redirectTab,
  },
  filter: ({ live, pastEventsEnabled, redirectUrl }) =>
    live && !pastEventsEnabled && redirectUrl === LiveEventsTypes.LIVE,
  fn: () => ({
    id: LiveEventsTypes.LIVE,
  }),
  target: eventsFeatureModel.changeActiveTab,
})

// second in order without past events
sample({
  clock: allTabsLoadedWithoutPast,
  source: {
    live: $hasLiveEvent,
    upcoming: $hasUpcomingEvent,
    pastEventsEnabled: options.$liveEventRecordsEnabled,
    redirectUrl: eventsFeatureModel.$redirectTab,
  },
  filter: ({ upcoming, live, pastEventsEnabled, redirectUrl }) =>
    ((upcoming && !live) || redirectUrl === LiveEventsTypes.UPCOMING) &&
    !pastEventsEnabled,
  fn: () => ({
    id: LiveEventsTypes.UPCOMING,
  }),
  target: eventsFeatureModel.changeActiveTab,
})

reset({
  // need to reset data on user logsIn or logsOut
  clock: guest.$guest,
  target: [
    api.liveEvents.$liveEvents,
    api.liveEvents.$upcomingEvents,
    api.liveEvents.$pastEvents,
  ],
})

eventsFeatureModel.$activeTab.reset(leftAllEventsPage)
eventsFeatureModel.$redirectTab.reset(leftAllEventsPage)

register(eventsFeatureModel.$isAnyEvents, '$events/isAnyEvents')
register(eventsFeatureModel.changeActiveTab, 'events/changeActiveTab')
register(eventsFeatureModel.$allEvents, '$events/allEvents')
register(eventsFeatureModel.loadMoreEvents, 'events/loadMoreEvents')
register($liveEventsTranslations, '$liveEvents/liveEventsTranslations')
